import type { PropsWithChildren } from 'react';
import type { TooltipPropsInterface } from '@zillow/constellation-10';
import { Tooltip } from '@zillow/constellation-10';

export type TooltipWrapperProps = {
  tooltipPlacement?: TooltipPropsInterface['placement'];
  tooltipText?: string;
  wrapInTooltip?: boolean;
  delayMs?: number;
};

const IMPERSONATION_TEXT = 'Disabled while impersonating';

/**
 * Conditionally wraps a control in a Tooltip to explain why it's disabled or unavailable
 * (e.g. "Disabled while impersonating", blocked/archived states, keyboard-shortcut hints).
 * The wrapped child is the trigger; toggle the tooltip on via `wrapInTooltip`.
 *
 * This is not a help affordance. For a "what is this?" help tooltip, don't use this wrapper:
 * render a default-trigger Constellation `Tooltip`/`Popover` (the question-mark `TriggerButton`)
 * inside a centered row — `css={{ display: 'flex', alignItems: 'center', gap: 'tighter' }}`. When
 * placing this wrapper inline next to label text, the caller owns that row layout
 * (`alignItems: 'center'`, `gap: 'tighter'`).
 * See the HelpTooltip pattern: `modules/visual-cohesion/src/patterns/HelpTooltip.stories.tsx`.
 */
const TooltipWrapper = ({
  children,
  tooltipPlacement,
  tooltipText = IMPERSONATION_TEXT,
  wrapInTooltip = false,
  delayMs,
}: PropsWithChildren<TooltipWrapperProps>): JSX.Element => {
  return (
    <>
      {wrapInTooltip ? (
        <Tooltip
          content={tooltipText}
          placement={tooltipPlacement || 'bottom'}
          shouldAwaitInteractionResponse
          trigger={children}
          {...(delayMs && { useHoverProps: { delay: { open: delayMs, close: 0 } } })}
        />
      ) : (
        children
      )}
    </>
  );
};

export default TooltipWrapper;
